/**
 * Everything that decides WHAT the file listing shows.
 *
 * Location, facets, sort, search and pagination were interleaved with ~50
 * other pieces of state inside a 2,800-line component, so answering "why did
 * this request go out with these params?" meant reading the whole thing. They
 * are one hook now, and the answer is `params`.
 *
 * The component that renders rows does not need any of this beyond the result.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useLoad } from '@/shared/hooks/requests.js';
import {
  E_IMZO_DOCUMENTS,
  FILES_ARCHIVED,
  NODE_CHILDREN,
  NODE_TRASH,
} from '@/shared/utils/urls.js';

export const PAGE_SIZE = 50;

/**
 * `location` is the DATASET being viewed, and nothing else.
 *
 * It used to carry the My Documents facets too (private/public/documents/…),
 * which meant three independent axes fought over one single-select — choosing
 * "Private" silently cleared "Signed". The facets are separate state now, and
 * anything left in localStorage from before the split is discarded on read.
 */
export const LOCATIONS = { ALL: 'all', ARCHIVED: 'archived', TRASH: 'trash', EIMZO: 'eimzo' };
const DEDICATED_LOCATIONS = [LOCATIONS.ARCHIVED, LOCATIONS.TRASH, LOCATIONS.EIMZO];

const STORAGE = {
  location: 'docs_visibility_filter',
  type: 'docs_type_facet',
  visibility: 'docs_vis_facet',
  action: 'docs_action_facet',
  sortBy: 'docs_sort_by',
  sortDir: 'docs_sort_dir',
};

const read = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};
const write = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private browsing: the listing still works, it just won't be remembered */
  }
};

/**
 * The archived / trash / e-imzo locations read from dedicated endpoints,
 * because the ordinary children query filters those rows out by design
 * (`archived_at IS NULL`, `deleted_at IS NULL`).
 */
function endpointFor(location, { page, params }) {
  const paged = { page, size: PAGE_SIZE };
  switch (location) {
    case LOCATIONS.EIMZO:
      return { url: E_IMZO_DOCUMENTS, params: paged };
    case LOCATIONS.ARCHIVED:
      return { url: FILES_ARCHIVED, params: paged };
    case LOCATIONS.TRASH:
      return { url: NODE_TRASH, params: paged };
    default:
      return { url: NODE_CHILDREN, params };
  }
}

export function useNodeListing({ debounceMs = 400 } = {}) {
  const { id: folderId } = useParams();

  const [location, setLocation] = useState(() => {
    const stored = read(STORAGE.location, LOCATIONS.ALL);
    return DEDICATED_LOCATIONS.includes(stored) ? stored : LOCATIONS.ALL;
  });

  // Three INDEPENDENT facets that AND together on the backend. Each is omitted
  // when "off" so they compose freely (Signed + Private + Action required).
  const [typeFilter, setTypeFilter] = useState(() => read(STORAGE.type, 'all'));
  const [visFilter, setVisFilter] = useState(() => read(STORAGE.visibility, 'any'));
  const [actionOnly, setActionOnly] = useState(() => read(STORAGE.action, '0') === '1');

  const [sortBy, setSortBy] = useState(() => read(STORAGE.sortBy, 'created'));
  const [sortDir, setSortDir] = useState(() => read(STORAGE.sortDir, 'desc'));

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => write(STORAGE.location, location), [location]);
  useEffect(() => write(STORAGE.type, typeFilter), [typeFilter]);
  useEffect(() => write(STORAGE.visibility, visFilter), [visFilter]);
  useEffect(() => write(STORAGE.action, actionOnly ? '1' : '0'), [actionOnly]);
  useEffect(() => write(STORAGE.sortBy, sortBy), [sortBy]);
  useEffect(() => write(STORAGE.sortDir, sortDir), [sortDir]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchValue), debounceMs);
    return () => clearTimeout(timer);
  }, [searchValue, debounceMs]);

  // Any change to what is being asked for restarts pagination; otherwise page
  // 3 of the previous query would be requested against the new one.
  useEffect(() => {
    setPage(1);
  }, [location, typeFilter, visFilter, actionOnly, sortBy, sortDir, debouncedSearch, folderId]);

  const params = useMemo(() => {
    const next = { page, size: PAGE_SIZE, sort: sortBy, order: sortDir };
    if (folderId) next.parent_id = folderId;
    if (typeFilter !== 'all') next.approval_state = typeFilter;
    if (visFilter !== 'any') next.visibility = visFilter;
    if (actionOnly) next.action_required = true;
    // Search is a filter on the listing, not a separate endpoint: recursive
    // within the open folder, or everything under the root.
    const term = debouncedSearch.trim();
    if (term) next.search = term;
    return next;
  }, [folderId, page, typeFilter, visFilter, actionOnly, sortBy, sortDir, debouncedSearch]);

  const request = endpointFor(location, { page, params });

  const result = useLoad({ url: request.url, params: request.params }, [
    page, location, typeFilter, visFilter, actionOnly, folderId, sortBy, sortDir, debouncedSearch,
  ]);

  /** Toggle direction when re-clicking the active column, else switch to it. */
  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortDir((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDir(column === 'name' ? 'asc' : 'desc');
  };

  /**
   * Clearing is immediate, not debounced: waiting 400 ms to show the full
   * listing again after pressing Escape reads as a hang.
   */
  const clearSearch = () => {
    setSearchValue('');
    setDebouncedSearch('');
  };

  const clearFacets = () => {
    setTypeFilter('all');
    setVisFilter('any');
    setActionOnly(false);
  };

  const hasActiveFacets = typeFilter !== 'all' || visFilter !== 'any' || actionOnly;

  return {
    // what is being asked for
    folderId,
    location,
    setLocation,
    typeFilter,
    setTypeFilter,
    visFilter,
    setVisFilter,
    actionOnly,
    setActionOnly,
    sortBy,
    sortDir,
    toggleSort,
    searchValue,
    setSearchValue,
    clearSearch,
    debouncedSearch,
    page,
    setPage,
    clearFacets,
    hasActiveFacets,
    // the request it produces, and its result
    params,
    url: request.url,
    result,
  };
}
